import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CopyableValue } from "@/components/CopyableValue";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, type OrgMember, type SelfHostedBooToken } from "@/lib/api";
import { parseBrainUrl, selfHostedEnv } from "@/lib/self-hosted";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, KeyRound, Server } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

function ShowOnceWarning({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

// `env` is built once per issue so the vault key shown doesn't change on re-render.
type Reveal = { kind: "created"; issued: SelfHostedBooToken; env: string } | { kind: "rotated"; issued: SelfHostedBooToken };

export function SelfHostedRevealDialog({ reveal, onClose }: { reveal: Reveal | null; onClose: () => void }) {
  return (
    <Dialog open={reveal !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="size-4" />
            {reveal?.kind === "rotated" ? "New token" : `${reveal?.issued.name ?? "Boo"} is ready to run`}
          </DialogTitle>
          <DialogDescription>
            Copy this now. It won't be shown again.
          </DialogDescription>
        </DialogHeader>

        {reveal?.kind === "created" && (
          <>
            <ShowOnceWarning>
              The token is the Boo's only credential: anyone holding it can act as this Boo and spend the
              org's credits. The vault key was made in this browser; keep it with the token.
            </ShowOnceWarning>
            <div className="space-y-1.5">
              <Label>boo.env</Label>
              <CopyableValue value={reveal.env} className="max-h-72 overflow-y-auto" />
            </div>
            <p className="text-xs text-muted-foreground">
              {reveal.issued.brainUrl
                ? `Dashboard chat and chat apps reach this Boo at ${reveal.issued.brainUrl}; that address must forward to port 3000.`
                : "Without a public URL the Boo runs and reaches Boo Lab, but dashboard chat and chat apps can't reach it. Add one on the Boo's page."}
            </p>
          </>
        )}

        {reveal?.kind === "rotated" && (
          <>
            <ShowOnceWarning>
              Replace the <code>BOO_INTEGRATIONS_TOKEN</code> line in the Boo's env file and restart it. Keep every
              other line, especially the vault key. A Boo already running on the old token keeps working for up to
              a day; deleting the Boo is what stops it at once.
            </ShowOnceWarning>
            <CopyableValue value={`BOO_INTEGRATIONS_TOKEN=${reveal.issued.token}`} />
          </>
        )}

        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CreateSelfHostedBooDialog({
  open,
  onClose,
  orgId,
  members,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  members: OrgMember[];
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [brainUrl, setBrainUrl] = useState("");
  const [reveal, setReveal] = useState<Reveal | null>(null);

  const reset = () => {
    setName("");
    setOwnerUserId("");
    setBrainUrl("");
  };

  const create = useMutation({
    mutationFn: () => {
      if (!name.trim()) throw new Error("Give the Boo a name");
      if (!ownerUserId) throw new Error("Pick an owner");
      const url = parseBrainUrl(brainUrl);
      if (!url.ok) throw new Error("The public URL must be an https address like https://boo.example.com, with no path");
      return api.admin.selfHosted.create(orgId, { name: name.trim(), ownerUserId, brainUrl: url.value }).then((r) => r.data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin-org-containers", orgId] });
      reset();
      onClose();
      setReveal({ kind: "created", issued: data, env: selfHostedEnv(data) });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Couldn't create the Boo"),
  });

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            reset();
            onClose();
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Server className="size-4" />
              New self-hosted Boo
            </DialogTitle>
            <DialogDescription>
              Runs on the org's own machine. Boo Lab sets nothing up; you get a token and an env file to start it with.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="self-hosted-name">Name</Label>
              <Input id="self-hosted-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Mac Mini Boo" />
            </div>
            <div className="space-y-1.5">
              <Label>Owner</Label>
              <Select value={ownerUserId} onValueChange={setOwnerUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick an org member" />
                </SelectTrigger>
                <SelectContent>
                  {members.map((m) => (
                    <SelectItem key={m.userId} value={m.userId}>
                      {m.userName || m.userEmail} <span className="text-muted-foreground">{m.userEmail}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="self-hosted-url">Public URL (optional)</Label>
              <Input
                id="self-hosted-url"
                value={brainUrl}
                onChange={(e) => setBrainUrl(e.target.value)}
                placeholder="https://boo.example.com"
              />
              <p className="text-xs text-muted-foreground">
                Where Boo Lab can reach the Boo, for dashboard chat and chat apps. You can add it later.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { reset(); onClose(); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Creating…" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <SelfHostedRevealDialog reveal={reveal} onClose={() => setReveal(null)} />
    </>
  );
}

// The super-admin Boo page's card for a self-hosted Boo: its public URL and
// a new token.
export function SelfHostedSection({
  containerId,
  brainUrl,
  queryKey,
}: {
  containerId: string;
  brainUrl: string | null;
  queryKey: readonly unknown[];
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(brainUrl ?? "");
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [reveal, setReveal] = useState<Reveal | null>(null);

  const saveUrl = useMutation({
    mutationFn: () => {
      const url = parseBrainUrl(draft);
      if (!url.ok) throw new Error("The public URL must be an https address like https://boo.example.com, with no path");
      return api.admin.selfHosted.setUrl(containerId, url.value).then((r) => r.data);
    },
    onSuccess: (data) => {
      setDraft(data.brainUrl ?? "");
      toast.success(data.brainUrl ? "Public URL saved" : "Public URL removed");
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Couldn't save the URL"),
  });

  const rotate = useMutation({
    mutationFn: () => api.admin.selfHosted.rotateToken(containerId).then((r) => r.data),
    onSuccess: (data) => {
      setConfirmRotate(false);
      setReveal({ kind: "rotated", issued: data });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Couldn't make a new token"),
  });

  const parsed = parseBrainUrl(draft);
  const unchanged = parsed.ok && parsed.value === brainUrl;

  return (
    <div className="space-y-4 py-2">
      <p className="text-sm text-muted-foreground">
        This Boo runs on the org's own machine. Boo Lab doesn't start, stop, upgrade or read logs from it.
      </p>
      <form
        className="space-y-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          saveUrl.mutate();
        }}
      >
        <Label htmlFor="self-hosted-url-edit">Public URL</Label>
        <div className="flex gap-2">
          <Input
            id="self-hosted-url-edit"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="https://boo.example.com"
          />
          <Button type="submit" variant="outline" disabled={saveUrl.isPending || unchanged}>
            Save
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Must match <code>BOO_BRAIN_URL</code> and <code>BOO_DEFAULT_VAULT_PUBLIC_URL</code> in the Boo's env file.
          Empty means Boo Lab can't reach it.
        </p>
      </form>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">Lost the token, or think it leaked?</p>
        <Button variant="outline" size="sm" onClick={() => setConfirmRotate(true)}>
          <KeyRound className="size-4" />
          New token
        </Button>
      </div>
      <ConfirmDialog
        open={confirmRotate}
        onOpenChange={setConfirmRotate}
        title="Make a new token?"
        description="The current token stops working for new starts right away. A Boo already running on it keeps working for up to a day, until you restart it with the new one."
        confirmLabel="Make new token"
        variant="default"
        loading={rotate.isPending}
        onConfirm={() => rotate.mutate()}
      />
      <SelfHostedRevealDialog reveal={reveal} onClose={() => setReveal(null)} />
    </div>
  );
}
